import { useReadContracts } from 'wagmi'
import type { Address } from 'viem'
import { ADDRESSES, LEGACY_TOKEN_ID, contractsConfigured } from '../config/faucet.config'
import { erc20Abi, erc1155Abi, openFaucetAbi, taboshiSeedsAbi } from '../abi'

export interface FaucetReads {
  // Faucet state
  totalDraws?: bigint
  opened?: boolean
  paused?: boolean
  canary?: Address
  currentPrice?: bigint
  maxDrawsPerTx?: bigint
  leavesPerEnhancedDraw?: bigint
  /** Whether THIS wallet may draw right now. Accounts for pause and canary mode. */
  canDraw?: boolean

  // Participant state
  cbBTCBalance?: bigint
  cbBTCAllowance?: bigint
  leafBalance?: bigint
  leavesApproved?: boolean
  seedBalance?: bigint
  totalSeedsMinted?: bigint

  isLoading: boolean
  refetch: () => void
}

/**
 * Every figure the page shows, read from chain in one multicall.
 *
 * Nothing is derived locally that the contract can answer. In particular the
 * price, the tier and the draws remaining in the tier all come from the Faucet,
 * so the page cannot drift from the contract as draws accumulate.
 */
export function useFaucetReads(address?: Address): FaucetReads {
  const faucet = { address: ADDRESSES.faucet, abi: openFaucetAbi } as const
  const enabled = contractsConfigured

  const { data, isLoading, refetch } = useReadContracts({
    allowFailure: true,
    contracts: [
      { ...faucet, functionName: 'totalDraws' },
      { ...faucet, functionName: 'opened' },
      { ...faucet, functionName: 'paused' },
      { ...faucet, functionName: 'canary' },
      { ...faucet, functionName: 'currentPrice' },
      { ...faucet, functionName: 'MAX_DRAWS_PER_TX' },
      { ...faucet, functionName: 'LEAVES_PER_ENHANCED_DRAW' },
      { address: ADDRESSES.seeds, abi: taboshiSeedsAbi, functionName: 'totalMinted' },
      // Wallet-scoped reads. Sent with the zero address when disconnected so the
      // multicall shape stays constant; the results are discarded below.
      {
        ...faucet,
        functionName: 'canDraw',
        args: [address ?? '0x0000000000000000000000000000000000000000'],
      },
      {
        address: ADDRESSES.cbBTC,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [address ?? '0x0000000000000000000000000000000000000000'],
      },
      {
        address: ADDRESSES.cbBTC,
        abi: erc20Abi,
        functionName: 'allowance',
        args: [address ?? '0x0000000000000000000000000000000000000000', ADDRESSES.faucet],
      },
      {
        address: ADDRESSES.taboshi1,
        abi: erc1155Abi,
        functionName: 'balanceOf',
        args: [address ?? '0x0000000000000000000000000000000000000000', LEGACY_TOKEN_ID],
      },
      {
        address: ADDRESSES.taboshi1,
        abi: erc1155Abi,
        functionName: 'isApprovedForAll',
        args: [address ?? '0x0000000000000000000000000000000000000000', ADDRESSES.faucet],
      },
      {
        address: ADDRESSES.seeds,
        abi: taboshiSeedsAbi,
        functionName: 'balanceOf',
        args: [address ?? '0x0000000000000000000000000000000000000000', 1n],
      },
    ],
    query: { enabled, refetchInterval: 15_000 },
  })

  // `allowFailure` keeps one reverting read from blanking the whole page; a
  // failed entry stays undefined and renders as "—" rather than as a zero.
  const at = <T,>(i: number): T | undefined =>
    data?.[i]?.status === 'success' ? (data[i].result as T) : undefined

  const connected = Boolean(address)

  return {
    totalDraws: at<bigint>(0),
    opened: at<boolean>(1),
    paused: at<boolean>(2),
    canary: at<Address>(3),
    currentPrice: at<bigint>(4),
    maxDrawsPerTx: at<bigint>(5),
    leavesPerEnhancedDraw: at<bigint>(6),
    totalSeedsMinted: at<bigint>(7),

    canDraw: connected ? at<boolean>(8) : undefined,
    cbBTCBalance: connected ? at<bigint>(9) : undefined,
    cbBTCAllowance: connected ? at<bigint>(10) : undefined,
    leafBalance: connected ? at<bigint>(11) : undefined,
    leavesApproved: connected ? at<boolean>(12) : undefined,
    seedBalance: connected ? at<bigint>(13) : undefined,

    isLoading,
    refetch,
  }
}

export interface Basket {
  /** Total satoshi for this basket. Signed as `maxCbBTC`. */
  quote?: bigint
  /** SEED an enhanced draw of this size would mint. */
  seedsEnhanced?: bigint
  /** SEED a standard draw of this size would mint. */
  seedsStandard?: bigint
  /** Legacy taboshi1 an enhanced draw of this size would retire. */
  leavesEnhanced?: bigint
  isLoading: boolean
}

/**
 * Everything about one basket, for the quantity the participant chose.
 *
 * Separated from the bulk reads because it changes with the user's own input,
 * and because the quote it returns is signed as `maxCbBTC` — so it must be a
 * fresh read, never a cached one.
 *
 * THE SEED AND LEAF FIGURES ARE READ FROM THE CONTRACT, NOT MULTIPLIED HERE.
 * `seedsFor` and `leavesFor` are the contract's own pure functions, so the two
 * options on the page cannot drift from what a draw would actually mint or
 * retire — including for quantities greater than one, where a local `× n` would
 * be a second implementation of something the contract already answers.
 */
export function useQuote(quantity: number): Basket {
  const valid = Number.isInteger(quantity) && quantity > 0
  const n = BigInt(valid ? quantity : 1)
  const faucet = { address: ADDRESSES.faucet, abi: openFaucetAbi } as const

  const { data, isLoading } = useReadContracts({
    allowFailure: true,
    contracts: [
      { ...faucet, functionName: 'quote', args: [n] },
      { ...faucet, functionName: 'seedsFor', args: [n, true] },
      { ...faucet, functionName: 'seedsFor', args: [n, false] },
      { ...faucet, functionName: 'leavesFor', args: [n, true] },
    ],
    query: { enabled: contractsConfigured && valid, refetchInterval: 15_000 },
  })

  const at = (i: number): bigint | undefined =>
    data?.[i]?.status === 'success' ? (data[i].result as bigint) : undefined

  return {
    quote: at(0),
    seedsEnhanced: at(1),
    seedsStandard: at(2),
    leavesEnhanced: at(3),
    isLoading,
  }
}
